import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { Button, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Stack } from "@mui/system";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import type { AnomaliesSortOption } from "./anomaliesSort";
// ─────────────────────────────────────────────────────────────────────────────
// Generic "Sort By" bottom sheet shared by Anomalies > Missing Data and FTD.
// Each caller only passes its own sortable fields (options) + default sort.

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

interface AnomaliesSortByDrawerProps {
  options: AnomaliesSortOption[];
  sortKey: string;
  sortDir: "asc" | "desc";
  onApply: (key: string, dir: "asc" | "desc") => void;
  dataTracker?: string;
}

export default function AnomaliesSortByDrawer({
  options,
  sortKey,
  sortDir,
  onApply,
  dataTracker,
}: AnomaliesSortByDrawerProps) {
  const [open, setOpen] = React.useState(false);
  const [pendingKey, setPendingKey] = React.useState(sortKey);
  const [pendingDir, setPendingDir] = React.useState<"asc" | "desc">(sortDir);

  const toggleDrawer = (newOpen: boolean) => () => {
    if (newOpen) {
      // reset pending selection to the applied sort on open
      setPendingKey(sortKey);
      setPendingDir(sortDir);
    }
    setOpen(newOpen);
  };

  const handleToggle = (value: string) => {
    if (pendingKey === value) {
      setPendingDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setPendingKey(value);
      setPendingDir("asc");
    }
  };

  const handleApplySorting = () => {
    onApply?.(pendingKey, pendingDir);
    setOpen(false);
  };

  const currentLabel =
    options.find((o) => o.value === sortKey)?.label ?? options[0]?.label ?? "";

  const ShortList = (
    <Box role="shortBy">
      <Puller />
      <Stack justifyContent="end" direction="row" p={1}>
        <IconButton
          aria-label="Close"
          onClick={toggleDrawer(false)}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
        }}
      >
        Sort By
      </Typography>

      <Box sx={{ my: 2 }}>
        {options.map(({ label, value }) => {
          const isCurrentSort = pendingKey === value;

          return (
            <Button
              key={value}
              onClick={() => handleToggle(value)}
              startIcon={
                <ArrowUpwardIcon
                  sx={{
                    position: "absolute",
                    left: "7px",
                    top: "7px",
                    opacity: isCurrentSort ? 1 : 0,
                    transform:
                      isCurrentSort && pendingDir === "desc"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    transition: "0.2s",
                    width: spacing.Spacing.lg.$value,
                  }}
                />
              }
              variant="outlined"
              color="secondary"
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: typography.Size.Text.sm.$value + "!important",
                },
                borderRadius: 2,
                mr: spacing.Spacing.sm.$value,
                mb: spacing.Spacing.sm.$value,
                py: spacing.Spacing.xs.$value,
                paddingRight: spacing.Spacing["1xl"].$value,
                paddingLeft: spacing.Spacing.xl.$value,
                textTransform: "capitalize",
                fontWeight: typography.Font_weight.Regular.$value,
                border: "1px solid " + colors.GrayLightMode[200].$value,
                color: colors.GrayLightMode[700].$value,
                fontSize: typography.Size.Text.xs.$value,
                ...(isCurrentSort && {
                  border:
                    "1px solid " + colors.Brand[600].$value + "!important",
                  boxShadow: "none !important",
                  color: colors.GrayLightMode[900].$value,
                }),
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        sx={{
          textTransform: "capitalize",
          boxShadow: "none !important",
          background: colors.Brand[600].$value,
          borderRadius: 2,
        }}
        onClick={handleApplySorting}
      >
        Apply
      </Button>
    </Box>
  );

  return (
    <>
      <Box
        onClick={toggleDrawer(true)}
        component="span"
        display={"flex"}
        alignItems={"center"}
        gap={0.5}
        data-tracker={dataTracker}
        sx={{
          fontWeight: typography.Font_weight.Medium.$value,
          color: colors.Brand[700].$value,
          cursor: "pointer",
        }}
      >
        {currentLabel}{" "}
        <ArrowUpwardIcon
          sx={{
            color: colors.Brand[700].$value,
            fontSize: typography.Size.Text.xl.$value,
            transform: sortDir === "desc" ? "rotate(180deg)" : "rotate(0deg)",
            transition: "0.2s",
          }}
        />
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {ShortList}
      </Drawer>
    </>
  );
}
